import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import {
  createHierarchyComponent,
  createHierarchySubSystem,
  createHierarchySystem,
  deleteHierarchyComponent,
  deleteHierarchySubSystem,
  deleteHierarchySystem,
  getComponentHierarchy,
  renameHierarchyComponent,
  renameHierarchySubSystem,
  renameHierarchySystem,
} from "../api";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";

interface Props {
  isAdmin: boolean;
  isSuperUser: boolean;
}

export default function ComponentHierarchy({ isAdmin, isSuperUser }: Props) {
  const queryClient = useQueryClient();
  const { data: hierarchy } = useQuery({
    queryKey: ["componentHierarchy"],
    queryFn: getComponentHierarchy,
  });

  const [selectedSystem, setSelectedSystem] = useState<string | null>(null);
  const [selectedSubSystem, setSelectedSubSystem] = useState<string | null>(
    null,
  );

  // Add state
  const [addingSystem, setAddingSystem] = useState(false);
  const [newSystemName, setNewSystemName] = useState("");
  const [addingSubSystem, setAddingSubSystem] = useState(false);
  const [newSubSystemName, setNewSubSystemName] = useState("");
  const [addingComponent, setAddingComponent] = useState(false);
  const [newComponentName, setNewComponentName] = useState("");

  // Edit state
  const [editingSystem, setEditingSystem] = useState<string | null>(null);
  const [editSystemName, setEditSystemName] = useState("");
  const [editingSubSystem, setEditingSubSystem] = useState<string | null>(null);
  const [editSubSystemName, setEditSubSystemName] = useState("");
  const [editingComponent, setEditingComponent] = useState<string | null>(null);
  const [editComponentName, setEditComponentName] = useState("");

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "system" | "sub_system" | "component";
    name: string;
  } | null>(null);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["componentHierarchy"] });

  // ─── System mutations ─────────────────────────────────────────
  const createSysMut = useMutation({
    mutationFn: (name: string) => createHierarchySystem(name),
    onSuccess: () => {
      invalidate();
      setAddingSystem(false);
      setNewSystemName("");
    },
  });

  const renameSysMut = useMutation({
    mutationFn: ({
      old_name,
      new_name,
    }: {
      old_name: string;
      new_name: string;
    }) => renameHierarchySystem(old_name, new_name),
    onSuccess: (_data, vars) => {
      invalidate();
      setEditingSystem(null);
      if (selectedSystem === vars.old_name) setSelectedSystem(vars.new_name);
    },
  });

  const deleteSysMut = useMutation({
    mutationFn: (name: string) => deleteHierarchySystem(name),
    onSuccess: () => {
      invalidate();
      if (selectedSystem === deleteTarget?.name) {
        setSelectedSystem(null);
        setSelectedSubSystem(null);
      }
      setDeleteTarget(null);
    },
  });

  // ─── Sub-system mutations ─────────────────────────────────────
  const createSubSysMut = useMutation({
    mutationFn: (name: string) =>
      createHierarchySubSystem(selectedSystem!, name),
    onSuccess: () => {
      invalidate();
      setAddingSubSystem(false);
      setNewSubSystemName("");
    },
  });

  const renameSubSysMut = useMutation({
    mutationFn: ({
      old_name,
      new_name,
    }: {
      old_name: string;
      new_name: string;
    }) => renameHierarchySubSystem(selectedSystem!, old_name, new_name),
    onSuccess: (_data, vars) => {
      invalidate();
      setEditingSubSystem(null);
      if (selectedSubSystem === vars.old_name)
        setSelectedSubSystem(vars.new_name);
    },
  });

  const deleteSubSysMut = useMutation({
    mutationFn: (name: string) =>
      deleteHierarchySubSystem(selectedSystem!, name),
    onSuccess: () => {
      invalidate();
      if (selectedSubSystem === deleteTarget?.name) setSelectedSubSystem(null);
      setDeleteTarget(null);
    },
  });

  // ─── Component mutations ──────────────────────────────────────
  const createCompMut = useMutation({
    mutationFn: (name: string) =>
      createHierarchyComponent(selectedSystem!, selectedSubSystem!, name),
    onSuccess: () => {
      invalidate();
      setAddingComponent(false);
      setNewComponentName("");
    },
  });

  const renameCompMut = useMutation({
    mutationFn: ({
      old_name,
      new_name,
    }: {
      old_name: string;
      new_name: string;
    }) =>
      renameHierarchyComponent(
        selectedSystem!,
        selectedSubSystem!,
        old_name,
        new_name,
      ),
    onSuccess: () => {
      invalidate();
      setEditingComponent(null);
    },
  });

  const deleteCompMut = useMutation({
    mutationFn: (name: string) =>
      deleteHierarchyComponent(selectedSystem!, selectedSubSystem!, name),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
    },
  });

  // ─── Derived data ─────────────────────────────────────────────
  const systems = hierarchy ? Object.keys(hierarchy).sort() : [];
  const subSystems =
    hierarchy && selectedSystem
      ? Object.keys(hierarchy[selectedSystem] ?? {}).sort()
      : [];
  const components =
    hierarchy && selectedSystem && selectedSubSystem
      ? [...(hierarchy[selectedSystem]?.[selectedSubSystem] ?? [])].sort()
      : [];

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === "system") deleteSysMut.mutate(deleteTarget.name);
    else if (deleteTarget.type === "sub_system")
      deleteSubSysMut.mutate(deleteTarget.name);
    else deleteCompMut.mutate(deleteTarget.name);
  };

  const initialized = useRef(false);
  useEffect(() => {
    if (initialized.current || !hierarchy) return;
    const firstSystem = Object.keys(hierarchy).sort()[0] ?? null;
    if (firstSystem) {
      const firstSubSystem =
        Object.keys(hierarchy[firstSystem] ?? {}).sort()[0] ?? null;
      setSelectedSystem(firstSystem);
      setSelectedSubSystem(firstSubSystem);
      initialized.current = true;
    }
  }, [hierarchy]);

  return (
    <Box sx={{ display: "flex", flex: 1, overflow: "hidden", height: "100%" }}>
      {/* Column 1: Systems */}
      <Box
        sx={{
          width: 260,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          borderRight: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 0,
            height: 48,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Systems
          </Typography>
          {isSuperUser && (
            <Tooltip title="Add system">
              <IconButton size="small" onClick={() => setAddingSystem(true)}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {addingSystem && (
          <Box sx={{ px: 2, py: 1 }}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="System name"
              value={newSystemName}
              onChange={(e) => setNewSystemName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newSystemName.trim())
                  createSysMut.mutate(newSystemName.trim());
                if (e.key === "Escape") {
                  setAddingSystem(false);
                  setNewSystemName("");
                }
              }}
              onBlur={() => {
                setAddingSystem(false);
                setNewSystemName("");
              }}
            />
          </Box>
        )}
        <List dense sx={{ overflow: "auto", flex: 1 }}>
          {systems.map((sys) => (
            <ListItemButton
              key={sys}
              selected={selectedSystem === sys}
              onClick={() => {
                setSelectedSystem(sys);
                setSelectedSubSystem(null);
              }}
            >
              {editingSystem === sys ? (
                <TextField
                  size="small"
                  fullWidth
                  autoFocus
                  value={editSystemName}
                  onChange={(e) => setEditSystemName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && editSystemName.trim())
                      renameSysMut.mutate({
                        old_name: sys,
                        new_name: editSystemName.trim(),
                      });
                    if (e.key === "Escape") setEditingSystem(null);
                  }}
                  onBlur={() => setEditingSystem(null)}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <>
                  <ListItemText primary={sys} />
                  {isSuperUser && (
                    <Box sx={{ display: "flex", gap: 0 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSystem(sys);
                          setEditSystemName(sys);
                        }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget({ type: "system", name: sys });
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  )}
                </>
              )}
            </ListItemButton>
          ))}
        </List>
      </Box>

      {/* Column 2: Sub-systems */}
      <Box
        sx={{
          width: 280,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          borderRight: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 0,
            height: 48,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Sub-systems
          </Typography>
          {isSuperUser && selectedSystem && (
            <Tooltip title="Add sub-system">
              <IconButton size="small" onClick={() => setAddingSubSystem(true)}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {addingSubSystem && (
          <Box sx={{ px: 2, py: 1 }}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="Sub-system name"
              value={newSubSystemName}
              onChange={(e) => setNewSubSystemName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newSubSystemName.trim())
                  createSubSysMut.mutate(newSubSystemName.trim());
                if (e.key === "Escape") {
                  setAddingSubSystem(false);
                  setNewSubSystemName("");
                }
              }}
              onBlur={() => {
                setAddingSubSystem(false);
                setNewSubSystemName("");
              }}
            />
          </Box>
        )}
        <List dense sx={{ overflow: "auto", flex: 1 }}>
          {subSystems.map((sub) => (
            <ListItemButton
              key={sub}
              selected={selectedSubSystem === sub}
              onClick={() => setSelectedSubSystem(sub)}
            >
              {editingSubSystem === sub ? (
                <TextField
                  size="small"
                  fullWidth
                  autoFocus
                  value={editSubSystemName}
                  onChange={(e) => setEditSubSystemName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && editSubSystemName.trim())
                      renameSubSysMut.mutate({
                        old_name: sub,
                        new_name: editSubSystemName.trim(),
                      });
                    if (e.key === "Escape") setEditingSubSystem(null);
                  }}
                  onBlur={() => setEditingSubSystem(null)}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <>
                  <ListItemText primary={sub} />
                  {isSuperUser && (
                    <Box sx={{ display: "flex", gap: 0 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSubSystem(sub);
                          setEditSubSystemName(sub);
                        }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget({ type: "sub_system", name: sub });
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  )}
                </>
              )}
            </ListItemButton>
          ))}
          {selectedSystem && subSystems.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, py: 2 }}
            >
              No sub-systems yet.
            </Typography>
          )}
          {!selectedSystem && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, py: 2 }}
            >
              Select a system.
            </Typography>
          )}
        </List>
      </Box>

      {/* Column 3: Components */}
      <Box
        sx={{
          flex: 1,
          minWidth: 200,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 0,
            height: 48,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Components
          </Typography>
          {isAdmin && selectedSubSystem && (
            <Tooltip title="Add component">
              <IconButton size="small" onClick={() => setAddingComponent(true)}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {addingComponent && (
          <Box sx={{ px: 2, py: 1 }}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="Component name"
              value={newComponentName}
              onChange={(e) => setNewComponentName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newComponentName.trim())
                  createCompMut.mutate(newComponentName.trim());
                if (e.key === "Escape") {
                  setAddingComponent(false);
                  setNewComponentName("");
                }
              }}
              onBlur={() => {
                setAddingComponent(false);
                setNewComponentName("");
              }}
            />
          </Box>
        )}
        <List dense sx={{ overflow: "auto", flex: 1 }}>
          {components.map((comp) => (
            <ListItemButton key={comp} disableRipple sx={{ cursor: "default" }}>
              {editingComponent === comp ? (
                <TextField
                  size="small"
                  fullWidth
                  autoFocus
                  value={editComponentName}
                  onChange={(e) => setEditComponentName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && editComponentName.trim())
                      renameCompMut.mutate({
                        old_name: comp,
                        new_name: editComponentName.trim(),
                      });
                    if (e.key === "Escape") setEditingComponent(null);
                  }}
                  onBlur={() => setEditingComponent(null)}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <>
                  <ListItemText primary={comp} />
                  {isSuperUser && (
                    <Box sx={{ display: "flex", gap: 0 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingComponent(comp);
                          setEditComponentName(comp);
                        }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget({ type: "component", name: comp });
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  )}
                </>
              )}
            </ListItemButton>
          ))}
          {selectedSubSystem && components.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, py: 2 }}
            >
              No components yet.
            </Typography>
          )}
          {!selectedSubSystem && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, py: 2 }}
            >
              Select a sub-system.
            </Typography>
          )}
        </List>
      </Box>

      {/* Delete confirmation dialog */}
      <ConfirmDeleteDialog
        open={!!deleteTarget}
        title={`Delete ${deleteTarget?.type.replace("_", "-") ?? ""}`}
        message={`The Component Hierarchy is common for ALL PIDs. Your changes will affect all PIDs. Are you sure you want to delete "${deleteTarget?.name ?? ""}"? This cannot be undone.`}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
}
